import { SITE_CATEGORIES } from './config';

// Shared primitives for the Program Type + School District filters that live on
// both the area-management and admin-students views. The sentinel below is the
// single source of the "site has no district/category" value: it must stay
// char-exact with the backend /students/people/filter sentinel (routes/students.js
// PEOPLE_FILTERS), which maps it to IS NULL. Displayed to users as "(Not set)".
const NOT_SET = 'Not set';

// Program Type options (value + label) plus the explicit "(Not set)" choice for
// null categories.
const CATEGORY_OPTIONS = [...SITE_CATEGORIES, { value: NOT_SET, label: '(Not set)' }];
const ALL_CATEGORY_VALUES = CATEGORY_OPTIONS.map(option => option.value);

// The display label for a raw category value.
const categoryLabel = value => {
  const match = CATEGORY_OPTIONS.find(option => option.value === value);
  return match ? match.label : value;
};

// The display label for a raw district value.
const districtLabel = value => (value === NOT_SET ? '(Not set)' : value);

// Maps a raw site attribute (possibly null/undefined) to the value used in the
// checkbox lists and sent to the backend: a real value passes through, absence
// becomes the sentinel.
const siteAttr = value => (value == null ? NOT_SET : value);

// Builds the ONE combined site-level predicate for the AREA list. district and
// category must match the SAME site, so this is a single .some() over the area's
// sites -- two independent .some() calls would wrongly match an area where one
// site satisfies the district half and a different site satisfies the category
// half (codex F8). Returns null when neither group is active (all boxes checked),
// so legacy null-attribute rows stay visible.
const buildAreaSiteCriteria = (showDistricts, allDistricts, showCategories) => {
  const districtsActive = showDistricts.length !== allDistricts.length;
  const categoriesActive = showCategories.length !== ALL_CATEGORY_VALUES.length;
  if (!districtsActive && !categoriesActive) {
    return null;
  }
  const districtOk = site =>
    !districtsActive || showDistricts.includes(siteAttr(site.schoolDistrict));
  const categoryOk = site => !categoriesActive || showCategories.includes(siteAttr(site.category));
  return area => (area.siteInfo || []).some(site => districtOk(site) && categoryOk(site));
};

export {
  NOT_SET,
  CATEGORY_OPTIONS,
  ALL_CATEGORY_VALUES,
  categoryLabel,
  districtLabel,
  siteAttr,
  buildAreaSiteCriteria,
};
